/* eslint-disable max-lines */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Alert,
  AlertTitle,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  LinearProgress,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  Badge,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Undo as UndoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Timeline as TimelineIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { format, differenceInDays, differenceInHours } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

function TabPanel({ children, value, index, ...other }) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`rollback-tabpanel-${index}`}
      aria-labelledby={`rollback-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </Box>
  );
}

// Rollback Statistics Dashboard Component
// eslint-disable-next-line max-lines-per-function
function RollbackStatistics({ rollbacks, loading }) {
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const monthlyRollbacks = rollbacks.filter(rollback => {
    const rollbackDate = new Date(rollback.completedAt || rollback.initiatedAt);
    return rollbackDate.getMonth() === currentMonth && rollbackDate.getFullYear() === currentYear;
  });

  const entitiesDeleted = rollbacks.reduce((sum, rollback) => sum + (rollback.entitiesDeleted || 0), 0);
  const failedRollbacks = rollbacks.filter(rollback => rollback.status === 'failed').length;

  const topUsers = rollbacks.reduce((acc, rollback) => {
    acc[rollback.initiatedBy] = (acc[rollback.initiatedBy] || 0) + 1;
    return acc;
  }, {});

  const topUsersList = Object.entries(topUsers)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  const statCards = [
    {
      title: 'Rollbacks This Month',
      value: monthlyRollbacks.length,
      icon: <UndoIcon />,
      color: 'primary',
      trend: monthlyRollbacks.length > 0 ? '+' + monthlyRollbacks.length : '0'
    },
    {
      title: 'Total Entities Deleted',
      value: entitiesDeleted.toLocaleString(),
      icon: <DeleteIcon />,
      color: 'warning',
      trend: `${rollbacks.length} rollbacks`
    },
    {
      title: 'Failed Rollbacks',
      value: failedRollbacks,
      icon: <ErrorIcon />,
      color: 'error',
      trend: failedRollbacks > 0 ? 'Requires attention' : 'All successful'
    },
    {
      title: 'Success Rate',
      value: rollbacks.length > 0 ? Math.round(((rollbacks.length - failedRollbacks) / rollbacks.length) * 100) + '%' : '100%',
      icon: <CheckCircleIcon />,
      color: 'success',
      trend: 'Last 30 days'
    }
  ];

  return (
    <Box>
      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((stat, index) => (
          <Grid
            key={index}
            size={{
              xs: 12,
              sm: 6,
              md: 3
            }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" component="div">
                      {loading ? <CircularProgress size={24} /> : stat.value}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {stat.trend}
                    </Typography>
                  </Box>
                  <Box sx={{ color: `${stat.color}.main` }}>
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      {/* Top Users */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Most Active Rollback Users
          </Typography>
          <List>
            {topUsersList.map(([user, count], index) => (
              <ListItem key={user} divider={index < topUsersList.length - 1}>
                <ListItemIcon>
                  <Badge badgeContent={count} color="primary">
                    <PeopleIcon />
                  </Badge>
                </ListItemIcon>
                <ListItemText
                  primary={user}
                  secondary={`${count} rollback${count !== 1 ? 's' : ''}`}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
}

// Import Impact Assessment Component
// eslint-disable-next-line max-lines-per-function
function ImportImpactAssessment({ importData, onClose, onApprove, onReject }) {
  const entitiesCount = importData?.createdEntityIds?.length || 0;
  const timeSinceImport = importData?.completedAt
    ? differenceInHours(new Date(), new Date(importData.completedAt))
    : 0;

  const riskLevel = entitiesCount > 100 ? 'high' : entitiesCount > 50 ? 'medium' : 'low';
  const timeRisk = timeSinceImport > 168 ? 'high' : timeSinceImport > 24 ? 'medium' : 'low'; // 7 days, 1 day

  const riskFactors = [
    {
      factor: 'Entity Count',
      level: riskLevel,
      details: `${entitiesCount} entities will be permanently deleted`
    },
    {
      factor: 'Time Since Import',
      level: timeRisk,
      details: `Import completed ${timeSinceImport} hours ago`
    },
    {
      factor: 'Data Dependencies',
      level: 'medium',
      details: 'May affect BigQuery datasets and GCS buckets'
    },
    {
      factor: 'User Impact',
      level: entitiesCount > 50 ? 'high' : 'low',
      details: 'Users may lose access to entity data and permissions'
    }
  ];

  const getRiskColor = (level) => {
    switch (level) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'info';
    }
  };

  return (
    <Dialog open={!!importData} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'warning.main' }}>
          <WarningIcon sx={{ mr: 1 }} />
          Rollback Impact Assessment
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>High Impact Operation</AlertTitle>
          This rollback will permanently delete {entitiesCount} entities and cannot be undone.
        </Alert>

        <Typography variant="h6" gutterBottom>
          Risk Assessment
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {riskFactors.map((risk, index) => (
            <Grid
              key={index}
              size={{
                xs: 12,
                sm: 6
              }}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip
                      label={risk.level.toUpperCase()}
                      color={getRiskColor(risk.level)}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <Typography variant="subtitle2">
                      {risk.factor}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {risk.details}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Typography variant="h6" gutterBottom>
          Import Details
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography><strong>Import ID:</strong> {importData?.jobId}</Typography>
          <Typography><strong>Created By:</strong> {importData?.createdBy}</Typography>
          <Typography><strong>Completion:</strong> {importData?.completedAt ? format(new Date(importData.completedAt), 'PPP p') : 'N/A'}</Typography>
          <Typography><strong>Success Rate:</strong> {importData?.successCount || 0} / {importData?.totalProcessed || 0}</Typography>
        </Box>

        {entitiesCount > 50 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle>Administrative Approval Required</AlertTitle>
            Large rollbacks (&gt;50 entities) require administrative approval for enterprise governance.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {entitiesCount > 50 ? (
          <>
            <Button
              onClick={onReject}
              color="error"
              variant="outlined"
            >
              Reject Rollback
            </Button>
            <Button
              onClick={onApprove}
              color="warning"
              variant="contained"
            >
              Approve Rollback
            </Button>
          </>
        ) : (
          <Button
            onClick={onApprove}
            color="warning"
            variant="contained"
          >
            Proceed with Rollback
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// Rollback Confirmation Dialog
function RollbackConfirmationDialog({
  open,
  onClose,
  onConfirm,
  importData,
  loading,
  requireReason = false
}) {
  const [reason, setReason] = useState('');
  const [impactAcknowledged, setImpactAcknowledged] = useState(false);

  const handleConfirm = () => {
    onConfirm(reason, impactAcknowledged);
  };

  const canConfirm = !requireReason || (reason.trim().length > 0 && impactAcknowledged);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
          <WarningIcon sx={{ mr: 1 }} />
          Confirm Rollback
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 3 }}>
          You are about to permanently delete <strong>{importData?.createdEntityIds?.length || 0} entities</strong>
          {' '}created during import "{importData?.jobId}". This action cannot be undone.
        </DialogContentText>

        {requireReason && (
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Rollback Reason (Required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Provide a detailed reason for this rollback operation..."
            sx={{ mb: 2 }}
            required
          />
        )}

        <FormControlLabel
          control={
            <Switch
              checked={impactAcknowledged}
              onChange={(e) => setImpactAcknowledged(e.target.checked)}
              color="warning"
            />
          }
          label="I understand this will permanently delete entities and may affect related systems"
        />

        {loading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Processing rollback... This may take several minutes.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={!canConfirm || loading}
        >
          {loading ? 'Rolling Back...' : 'Confirm Rollback'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// eslint-disable-next-line max-lines-per-function
export default function RollbackManagement() {
  const [activeTab, setActiveTab] = useState(0);
  const [rollbacks, setRollbacks] = useState([]);
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, _setStatusFilter] = useState('all');

  // Dialog states
  const [impactAssessmentData, setImpactAssessmentData] = useState(null);
  const [confirmationDialog, setConfirmationDialog] = useState({ open: false, data: null });
  const [rollbackLoading, setRollbackLoading] = useState(false);

  // Menu states
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedImport, setSelectedImport] = useState(null);

  const { user } = useAuth();

  useEffect(() => {
    loadRollbackData();
  }, []);

  const loadRollbackData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load rollback history from audit logs
      const auditData = await api.getRollbackHistory();
      setRollbacks(auditData.rollbacks || []);

      // Load current imports that can be rolled back
      const importData = await api.getRollbackableImports();
      setImports(importData.imports || []);

    } catch (err) {
      console.error('Error loading rollback data:', err);
      setError('Failed to load rollback data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleRollbackClick = async (importData) => {
    const entitiesCount = importData.createdEntityIds?.length || 0;

    if (entitiesCount > 50) {
      // Large imports require impact assessment
      setImpactAssessmentData(importData);
    } else {
      // Smaller imports go directly to confirmation
      setConfirmationDialog({ open: true, data: importData });
    }
  };

  const handleImpactAssessmentApprove = () => {
    setImpactAssessmentData(null);
    setConfirmationDialog({ open: true, data: impactAssessmentData });
  };

  const handleImpactAssessmentReject = () => {
    setImpactAssessmentData(null);
    // Could log rejection in audit trail
  };

  const executeRollback = async (reason, impactAcknowledged) => {
    try {
      setRollbackLoading(true);

      const rollbackData = {
        importId: confirmationDialog.data.jobId,
        reason: reason,
        impactAcknowledged: impactAcknowledged,
        adminNotes: `Rollback initiated by ${user.email} with impact assessment`
      };

      const result = await api.executeRollbackWithAudit(rollbackData);

      if (result.success) {
        // Refresh data
        await loadRollbackData();

        // Show success message
        setError('');

        // Log successful rollback
        console.log('Rollback completed:', result);
      } else {
        throw new Error(result.message || 'Rollback failed');
      }

    } catch (err) {
      console.error('Rollback failed:', err);
      setError('Rollback failed: ' + err.message);
    } finally {
      setRollbackLoading(false);
      setConfirmationDialog({ open: false, data: null });
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      completed: { color: 'success', icon: <CheckCircleIcon /> },
      failed: { color: 'error', icon: <ErrorIcon /> },
      'rolling_back': { color: 'warning', icon: <CircularProgress size={16} /> },
      pending: { color: 'info', icon: <ScheduleIcon /> }
    };

    const config = statusConfig[status] || { color: 'default', icon: <InfoIcon /> };

    return (
      <Chip
        label={status?.toUpperCase() || 'UNKNOWN'}
        color={config.color}
        size="small"
        icon={config.icon}
      />
    );
  };

  const getTimeSinceImport = (date) => {
    if (!date) return 'Unknown';
    const days = differenceInDays(new Date(), new Date(date));
    if (days === 0) {
      const hours = differenceInHours(new Date(), new Date(date));
      return `${hours}h ago`;
    }
    return `${days}d ago`;
  };

  const filteredRollbacks = rollbacks.filter(rollback => {
    const matchesSearch = searchQuery === '' ||
      rollback.importId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      rollback.initiatedBy.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' || rollback.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const filteredImports = imports.filter(importItem => {
    const matchesSearch = searchQuery === '' ||
      importItem.jobId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      importItem.createdBy.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesSearch;
  });

  const paginatedData = activeTab === 0
    ? filteredRollbacks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : filteredImports.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <UndoIcon sx={{ mr: 1 }} />
        Import Rollback Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Main Tabs */}
      <Paper sx={{ width: '100%', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Statistics" icon={<AssessmentIcon />} iconPosition="start" />
          <Tab label="Rollback History" icon={<TimelineIcon />} iconPosition="start" />
          <Tab label="Available Rollbacks" icon={<UndoIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <TabPanel value={activeTab} index={0}>
        <RollbackStatistics rollbacks={rollbacks} loading={loading} />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {/* Rollback History */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search rollbacks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
            }}
          />
          <Button
            startIcon={<FilterListIcon />}
            variant="outlined"
            size="small"
          >
            Filter
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            variant="outlined"
            size="small"
            onClick={loadRollbackData}
          >
            Refresh
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Rollback ID</TableCell>
                <TableCell>Import ID</TableCell>
                <TableCell>Initiated By</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Entities Deleted</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((rollback) => (
                <TableRow key={rollback.rollbackId}>
                  <TableCell>{rollback.rollbackId}</TableCell>
                  <TableCell>{rollback.importId}</TableCell>
                  <TableCell>{rollback.initiatedBy}</TableCell>
                  <TableCell>
                    {rollback.initiatedAt ? format(new Date(rollback.initiatedAt), 'PPP p') : 'N/A'}
                  </TableCell>
                  <TableCell>{rollback.entitiesDeleted}</TableCell>
                  <TableCell>{getStatusChip(rollback.status)}</TableCell>
                  <TableCell>
                    <Tooltip title={rollback.reason || 'No reason provided'}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                        {rollback.reason || 'No reason provided'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton size="small">
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredRollbacks.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
          />
        </TableContainer>
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        {/* Available Rollbacks */}
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Rollback Safety Notice</AlertTitle>
          Rollbacks permanently delete entities and cannot be undone. Use with extreme caution.
        </Alert>

        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search imports..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
            }}
          />
          <Button
            startIcon={<RefreshIcon />}
            variant="outlined"
            size="small"
            onClick={loadRollbackData}
          >
            Refresh
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Import ID</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Completion Date</TableCell>
                <TableCell>Entities Created</TableCell>
                <TableCell>Time Since Import</TableCell>
                <TableCell>Risk Level</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((importItem) => {
                const entitiesCount = importItem.createdEntityIds?.length || 0;
                const timeSince = getTimeSinceImport(importItem.completedAt);
                const riskLevel = entitiesCount > 100 ? 'HIGH' : entitiesCount > 50 ? 'MEDIUM' : 'LOW';
                const riskColor = entitiesCount > 100 ? 'error' : entitiesCount > 50 ? 'warning' : 'success';

                return (
                  <TableRow key={importItem.jobId}>
                    <TableCell>{importItem.jobId}</TableCell>
                    <TableCell>{importItem.createdBy}</TableCell>
                    <TableCell>
                      {importItem.completedAt ? format(new Date(importItem.completedAt), 'PPP p') : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Badge badgeContent={entitiesCount} color="primary">
                        <PeopleIcon />
                      </Badge>
                    </TableCell>
                    <TableCell>{timeSince}</TableCell>
                    <TableCell>
                      <Chip
                        label={riskLevel}
                        color={riskColor}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        startIcon={<UndoIcon />}
                        color="warning"
                        variant="outlined"
                        size="small"
                        onClick={() => handleRollbackClick(importItem)}
                        sx={{ mr: 1 }}
                      >
                        Rollback
                      </Button>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setActionMenuAnchor(e.currentTarget);
                          setSelectedImport(importItem);
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredImports.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
          />
        </TableContainer>
      </TabPanel>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => setActionMenuAnchor(null)}
      >
        <MenuItem onClick={() => {
          // View import details
          setActionMenuAnchor(null);
        }}>
          <ListItemIcon>
            <VisibilityIcon />
          </ListItemIcon>
          View Details
        </MenuItem>
        <MenuItem onClick={() => {
          // Download rollback report
          setActionMenuAnchor(null);
        }}>
          <ListItemIcon>
            <DownloadIcon />
          </ListItemIcon>
          Download Report
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            handleRollbackClick(selectedImport);
            setActionMenuAnchor(null);
          }}
          sx={{ color: 'warning.main' }}
        >
          <ListItemIcon>
            <UndoIcon color="warning" />
          </ListItemIcon>
          Initiate Rollback
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      <ImportImpactAssessment
        importData={impactAssessmentData}
        onClose={() => setImpactAssessmentData(null)}
        onApprove={handleImpactAssessmentApprove}
        onReject={handleImpactAssessmentReject}
      />

      <RollbackConfirmationDialog
        open={confirmationDialog.open}
        onClose={() => setConfirmationDialog({ open: false, data: null })}
        onConfirm={executeRollback}
        importData={confirmationDialog.data}
        loading={rollbackLoading}
        requireReason={true}
      />
    </Box>
  );
}